import React, { FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { isNil, isObject } from 'lodash-es'
import { MultiButton, MultiButtonProps } from './MultiButton'
import { ButtonStrip } from './ButtonStrip'
import { Field } from 'view/components/inputs/Field'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'

type RadioButtonStripOption = {
  translation: Translation
  disabled?: boolean
  optionRenderer?: (optionElement: JSX.Element, key: Translation) => JSX.Element
}

export interface RadioButtonStripProps {
  options: (RadioButtonStripOption | Translation)[]
  selectedOption: Translation | null
  disabled?: boolean
  buttonWidth?: number
  onUpdate: (option: Translation) => unknown
  dataTestId?: string
}

export const RadioButton = styled(MultiButton)<MultiButtonProps & { isSelected: boolean; width?: number }>`
  font-weight: ${constants.FontWeight.SemiBold};
  font-size: 12px;
  text-decoration: none;
  height: 26px;
  width: ${props => !isNil(props.width) && `${props.width}px`};
  opacity: ${props => (props.disabled ? '0.33' : '1')};
  background-color: ${props => (props.isSelected ? constants.ACCENT_PRIMARY : constants.CORE_PRIMARY_2)};
  color: ${props => (props.isSelected ? constants.CORE_PRIMARY : constants.CORE_PRIMARY_5)};
`

export const RadioButtonStrip: FunctionComponent<RadioButtonStripProps> = props => {
  return (
    <ButtonStrip>
      {props.options.map(opt => {
        const option = isObject(opt) ? opt.translation : opt
        const disabled = (isObject(opt) ? opt.disabled : false) || props.disabled
        const renderer = (isObject(opt) && opt.optionRenderer) || ((e: JSX.Element) => e)
        return renderer(
          <RadioButton
            key={option}
            isSelected={option === props.selectedOption}
            aria-pressed={option === props.selectedOption}
            disabled={disabled}
            width={props.buttonWidth}
            onClick={() => props.onUpdate(option)}
            dataTestId={`${props.dataTestId}-RadioButton-Option`}>
            {Locale.translate(option)}
          </RadioButton>,
          option
        )
      })}
    </ButtonStrip>
  )
}

export const RadioButtonStripField = Field(RadioButtonStrip)
